import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Button,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  notification,
} from "antd";
import { Link } from "react-router-dom";
import routes from "../../../../engine/routes";
import { mergeApi, errMsg } from "../institutionMerge.api";
import { fmtDate, fmtDay } from "../institutionMerge.constants";
import { ACTION_COL, AFTER_COL, BEFORE_COL } from "./beforeAfter";

const { Text } = Typography;

const PAGE_SIZES = ["50", "100", "200"];
const LIST_CAP = 15;
const ISO_DATE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/;

/** A value as the operator reads it: empty → "—", booleans → oui/non, ISO dates → fr-CH, objects → their name. */
export const showValue = v => {
  if (v === null || v === undefined || v === "") return "—";
  if (typeof v === "boolean") return v ? "oui" : "non";
  if (Array.isArray(v)) return v.length ? v.map(showValue).join(", ") : "—";
  if (typeof v === "string") return ISO_DATE.test(v) ? fmtDate(v) : v;
  if (typeof v === "object") return v.name || v.label || JSON.stringify(v);
  return String(v);
};

/** Tag colour per action code (server `item.action.code`); `keep_*` codes stay neutral. */
export const ACTION_COLORS = {
  repoint: "blue",
  archive: "purple",
  merge_account: "orange",
  address_merge: "orange",
  responsable_merge: "orange",
  rename: "cyan",
  edit: "cyan",
  address_edit: "cyan",
  responsable_edit: "cyan",
  blacklist_keep: "green",
  blacklist_drop: "volcano",
};

/** Every record is re-pointed unless the server says otherwise. */
export const DEFAULT_ACTION = {
  code: "repoint",
  label: "Rattaché à l'institution résultante",
};

/** What the merge does with one record: the action label as a coloured tag, its detail underneath. */
export function ActionTag({ action }) {
  const a = action && action.label ? action : DEFAULT_ACTION;
  return (
    <Space direction="vertical" size={0}>
      <Tag color={ACTION_COLORS[a.code]} style={{ marginRight: 0 }}>
        {a.label}
      </Tag>
      {a.detail && (
        <Text type="secondary" style={{ fontSize: 12 }}>
          {a.detail}
        </Text>
      )}
    </Space>
  );
}

const listText = xs =>
  xs.length > LIST_CAP
    ? `${xs.slice(0, LIST_CAP).join(", ")} … (+${xs.length - LIST_CAP})`
    : xs.join(", ");

/**
 * Front route for a record. The server only sends `model` + `_id`; the front owns its pages.
 * Users open in the context of the institution the record belongs to for this key
 * (interpreters have their own page); addresses, responsables and batches have no page.
 */
export function recordLink(r) {
  const id = r._id;
  switch (r.model) {
    case "Prestation":
      return routes.prestations(id).detail.path;
    case "Bill":
      return routes.billing(id).detail.path;
    case "Institution":
      return routes.institutions(id).detail.path;
    case "Principal":
      return routes.principals(id).detail.path;
    case "User":
      if (r.info && r.info.role === "interprete")
        return routes.interpretes(id).detail.path;
      return r.sourceId
        ? routes.institutions(r.sourceId).detailUser(id).path
        : null;
    default:
      return r.link || null;
  }
}

/** The identifying extras the server sends per model, in one secondary line. */
export const infoText = info => {
  if (!info) return "";
  const parts = [];
  if (info.status) parts.push(info.status);
  if (info.date) parts.push(fmtDay(info.date));
  if (info.type) parts.push(info.type);
  if (info.month || info.year)
    parts.push(
      info.month
        ? `${String(info.month).padStart(2, "0")}/${info.year || "?"}`
        : String(info.year),
    );
  if (info.role) parts.push(info.role);
  if (info.email) parts.push(info.email);
  return parts.join(" · ");
};

export function RecordLabel({ record }) {
  const to = recordLink(record);
  const extras = infoText(record.info);
  const label = record.label || String(record._id);
  return (
    <Space direction="vertical" size={0}>
      {to ? (
        <Link to={to} target="_blank" rel="noopener noreferrer">
          {label}
        </Link>
      ) : (
        <Text>{label}</Text>
      )}
      <Text type="secondary" style={{ fontSize: 12 }}>
        {extras ? `${extras} · ` : ""}
        <Text
          type="secondary"
          code
          copyable={{
            text: String(record._id),
            tooltips: ["copier l'identifiant", "copié"],
          }}
        >
          …{String(record._id).slice(-6)}
        </Text>
      </Text>
    </Space>
  );
}

function ScalarDiff({ before, after }) {
  const b = showValue(before);
  const a = showValue(after);
  if (b === a)
    return (
      <Text>
        {b} <Text type="secondary">(inchangé)</Text>
      </Text>
    );
  return (
    <>
      <Text delete type="secondary">
        {b}
      </Text>
      <Text type="secondary"> → </Text>
      <Text strong>{a}</Text>
    </>
  );
}

/** Long lists stay readable: only what leaves and what arrives is spelled out. */
function ArrayDiff({ before, after }) {
  const b = before.map(showValue);
  const a = after.map(showValue);
  const inAfter = new Set(a);
  const inBefore = new Set(b);
  const removed = b.filter(x => !inAfter.has(x));
  const added = a.filter(x => !inBefore.has(x));
  const kept = b.filter(x => inAfter.has(x)).length;
  if (!removed.length && !added.length)
    return <Text type="secondary">inchangé ({kept} élément(s))</Text>;
  return (
    <Space size={8} wrap>
      {removed.length > 0 && (
        <Text delete type="secondary">
          − {listText(removed)}
        </Text>
      )}
      {added.length > 0 && <Text strong>+ {listText(added)}</Text>}
      {kept > 0 && <Text type="secondary">({kept} inchangé(s))</Text>}
    </Space>
  );
}

/** One `changes` entry: « label : avant → après » (or a − / + diff for arrays). */
export function ChangeLine({ change }) {
  const { label, field, before, after } = change;
  const arrays = Array.isArray(before) && Array.isArray(after);
  return (
    <div>
      <Text type="secondary">{label || field} : </Text>
      {arrays ? (
        <ArrayDiff before={before} after={after} />
      ) : (
        <ScalarDiff before={before} after={after} />
      )}
    </div>
  );
}

/**
 * Every record behind one registry key, with its before → after per field.
 * Server-side pagination (50/100/200) and a per-source filter; loads on mount so
 * every section of the preview fills in parallel.
 */
export function RecordsTable({ mergeId, entry, sources, reloadKey }) {
  const [sourceId, setSourceId] = useState(undefined);
  const [limit, setLimit] = useState(50);
  const [state, setState] = useState({
    items: [],
    total: 0,
    page: 1,
    loading: true,
    error: null,
  });
  // a slow earlier page must never overwrite a later one
  const seq = useRef(0);

  const load = useCallback(
    async (page, pageSize, src) => {
      const mine = ++seq.current;
      setState(s => ({ ...s, loading: true, error: null }));
      try {
        const d = await mergeApi.records(mergeId, {
          key: entry.key,
          sourceId: src || undefined,
          page,
          limit: pageSize,
        });
        if (mine !== seq.current) return;
        setState({
          items: d.items || [],
          total: d.total || 0,
          page: d.page || page,
          loading: false,
          error: null,
        });
      } catch (e) {
        if (mine !== seq.current) return;
        const msg = errMsg(e);
        notification.error({
          message: `Enregistrements non chargés — ${entry.label}`,
          description: msg,
        });
        setState(s => ({ ...s, loading: false, error: msg }));
      }
    },
    [mergeId, entry.key, entry.label],
  );

  useEffect(() => {
    load(1, limit, sourceId);
  }, [load, limit, sourceId, reloadKey]);

  const perSource = entry.perSource || {};
  const filterOptions = (sources || [])
    .filter(s => perSource[s._id] === undefined || perSource[s._id] > 0)
    .map(s => ({
      value: s._id,
      label:
        perSource[s._id] !== undefined
          ? `${s.name} (${perSource[s._id]})`
          : s.name,
    }));

  const columns = [
    {
      title: "Enregistrement",
      key: "label",
      width: "30%",
      render: (_, r) => <RecordLabel record={r} />,
    },
    {
      title: "Établissement d'origine",
      dataIndex: "sourceName",
      width: 220,
      ...BEFORE_COL,
      render: n => n || <Text type="secondary">—</Text>,
    },
    {
      title: "Avant → Après",
      key: "changes",
      ...AFTER_COL,
      render: (_, r) =>
        r.changes && r.changes.length ? (
          <Space direction="vertical" size={0}>
            {r.changes.map((c, i) => (
              <ChangeLine key={`${c.field || c.label}-${i}`} change={c} />
            ))}
          </Space>
        ) : (
          <Text type="secondary">aucun changement</Text>
        ),
    },
    {
      title: "Action",
      key: "action",
      width: 260,
      ...ACTION_COL,
      render: (_, r) => <ActionTag action={r.action} />,
    },
  ];

  return (
    <div>
      <Space wrap style={{ marginBottom: 8 }}>
        <Text type="secondary">Établissement :</Text>
        <Select
          allowClear
          showSearch
          size="small"
          optionFilterProp="label"
          style={{ minWidth: 280 }}
          placeholder="Tous les établissements"
          value={sourceId}
          onChange={v => setSourceId(v || undefined)}
          options={filterOptions}
        />
        <Button
          size="small"
          loading={state.loading}
          onClick={() => load(state.page, limit, sourceId)}
        >
          Recharger
        </Button>
        {state.error && <Text type="danger">{state.error}</Text>}
      </Space>
      <Table
        size="small"
        loading={state.loading}
        rowKey={r => `${r._id}-${r.sourceId || ""}`}
        columns={columns}
        dataSource={state.items}
        locale={{ emptyText: "Aucun enregistrement" }}
        pagination={{
          size: "small",
          current: state.page,
          pageSize: limit,
          total: state.total,
          showSizeChanger: true,
          pageSizeOptions: PAGE_SIZES,
          showTotal: (t, [a, b]) => `${a}–${b} sur ${t}`,
          onChange: (p, ps) =>
            ps !== limit ? setLimit(ps) : load(p, limit, sourceId),
        }}
      />
    </div>
  );
}
